import { useEffect, useState } from "react";
import type {
  ScheduleEncryptedConnection,
  ScheduleRunRecord,
  ScheduleSource,
} from "@agencylens/shared";
import { encryptJson } from "@agencylens/crypto";
import { Button } from "@/components/ui/Button.js";
import { getSession } from "@/lib/session.js";
import { getDek } from "@/lib/dek.js";
import { useApp } from "@/store/app.js";
import { loadConnectionRaw } from "@/lib/connections.js";
import {
  type ScheduleListItem,
  listSchedules,
  createSchedule,
  deleteSchedule as apiDeleteSchedule,
  listRuns,
} from "@/lib/schedules-api.js";

/**
 * Settings → Scheduled reports panel.
 *
 * Tier-2+ buyers create monthly delivery schedules. The form picks an
 * already-connected GA4 property, mirrors that connection (encrypted) into
 * the schedule payload, and POSTs to the cron worker. Once a day at 02:00
 * UTC the cron checks for due schedules, renders the PDF, and sends via
 * the buyer's SMTP host (see Settings → Email Delivery).
 *
 * v1 supports only GA4 + monthly cadence. Multi-source schedules and
 * weekly/custom cadence are explicit follow-ups.
 */

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => i + 1);

function fmtFrequency(item: ScheduleListItem): string {
  return `Monthly on day ${item.frequency.dayOfMonth}, ${
    String(item.frequency.hourUtc ?? 2).padStart(2, "0")
  }:00 UTC`;
}

function statusPillClasses(status: ScheduleListItem["status"]): string {
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "paused":
      return "bg-amber-50 text-amber-700 border-amber-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function runStatusClasses(status: ScheduleRunRecord["status"]): string {
  switch (status) {
    case "ok":
      return "bg-emerald-50 text-emerald-700";
    case "skipped":
      return "bg-amber-50 text-amber-700";
    case "error":
      return "bg-red-50 text-red-700";
    default:
      return "bg-slate-50 text-slate-700";
  }
}

export function SchedulesPanel({
  onError,
}: {
  onError: (msg: string | undefined) => void;
}) {
  const session = getSession();
  const { ga4Connection, ga4Properties } = useApp();
  const tier = session?.payload.tier;
  const tierIsPaid = tier === "tier-2" || tier === "tier-3";

  const [list, setList] = useState<ScheduleListItem[]>([]);
  const [busy, setBusy] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    label: "",
    propertyId: "",
    recipients: "",
    dayOfMonth: 1,
    subjectOverride: "",
  });
  const [runsOpen, setRunsOpen] = useState<string | undefined>();
  const [runs, setRuns] = useState<Record<string, ScheduleRunRecord[]>>({});

  async function reload() {
    if (!session || !tierIsPaid) return;
    try {
      setBusy("Loading schedules…");
      const r = await listSchedules(session.jwt);
      setList(r.schedules);
    } catch (e) {
      const ee = e as Error & { error?: string };
      if (ee.error !== "tier_too_low") {
        onError(`Could not list schedules: ${ee.error ?? ee.message}`);
      }
    } finally {
      setBusy(undefined);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.jwt, tierIsPaid]);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    onError(undefined);
    if (!ga4Connection) {
      onError("Connect Google Analytics first — schedules need a GA4 source.");
      return;
    }
    const property = ga4Properties.find((p) => p.propertyId === form.propertyId);
    if (!property) {
      onError("Pick a GA4 property.");
      return;
    }
    const recipients = form.recipients
      .split(/[,\n]/)
      .map((r) => r.trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      onError("At least one recipient email is required.");
      return;
    }
    setBusy("Creating schedule…");
    try {
      // Mirror the buyer's GA4 connection into the schedule payload so the
      // cron worker can authenticate to Google at fire time. The connection
      // is already encrypted in IndexedDB; we re-encrypt the whole
      // ConnectionRecord JSON for the schedule blob.
      const conn = await loadConnectionRaw("google-analytics", "default");
      if (!conn) {
        onError("GA4 connection not found in browser. Reconnect Google Analytics.");
        return;
      }
      // Encrypt the connection mirror with the buyer's DEK so the cron worker
      // (which unwraps the same DEK from KV) can decrypt it at fire time.
      const dek = await getDek();
      const encryptedRecord = await encryptJson(conn, dek);
      const sources: ScheduleSource[] = [
        {
          platform: "google-analytics",
          handle: "default",
          ref: property.propertyId,
        },
      ];
      const encryptedConnections: ScheduleEncryptedConnection[] = [
        {
          platform: "google-analytics",
          handle: "default",
          encryptedRecord,
        },
      ];
      await createSchedule(session.jwt, {
        label: form.label.trim() || property.displayName,
        template: "generic",
        sources,
        recipients,
        frequency: { dayOfMonth: form.dayOfMonth, hourUtc: 2 },
        encryptedConnections,
        ...(form.subjectOverride.trim()
          ? { subjectOverride: form.subjectOverride.trim() }
          : {}),
      });
      setCreating(false);
      setForm({
        label: "",
        propertyId: "",
        recipients: "",
        dayOfMonth: 1,
        subjectOverride: "",
      });
      await reload();
    } catch (e) {
      const ee = e as Error & { error?: string; hint?: string };
      onError(`${ee.error ?? ee.message}${ee.hint ? ` — ${ee.hint}` : ""}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDelete(s: ScheduleListItem) {
    if (!session) return;
    if (!window.confirm(`Delete schedule "${s.label}"? This cannot be undone.`)) {
      return;
    }
    onError(undefined);
    setBusy("Deleting…");
    try {
      await apiDeleteSchedule(session.jwt, s.id);
      setList((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      onError(`Delete failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function toggleRuns(id: string) {
    if (!session) return;
    if (runsOpen === id) {
      setRunsOpen(undefined);
      return;
    }
    onError(undefined);
    setBusy("Loading run history…");
    try {
      const r = await listRuns(session.jwt, id);
      setRuns((prev) => ({ ...prev, [id]: r.runs }));
      setRunsOpen(id);
    } catch (e) {
      onError(`Run history failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-lg font-medium">Scheduled reports</h2>
      <p className="mb-4 text-sm text-slate-600">
        Send a branded PDF report to your clients on the same day each month,
        from your verified SMTP sender. Cron runs daily at 02:00 UTC and fires
        any schedule whose day-of-month is current or has passed.
      </p>

      {!tierIsPaid && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Scheduled reports are a <strong>Tier 2+</strong> feature. Your current
          tier is <strong>{tier ?? "unknown"}</strong>. Upgrade in the AgencyLens
          AppSumo listing to unlock.
        </div>
      )}

      {tierIsPaid && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-slate-500">
              {list.length}{" "}
              {list.length === 1 ? "schedule" : "schedules"} configured
            </span>
            {!creating && (
              <Button
                onClick={() => setCreating(true)}
                disabled={busy !== undefined}
                size="sm"
              >
                New schedule
              </Button>
            )}
          </div>

          {creating && (
            <form
              onSubmit={onCreate}
              className="mb-6 space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-slate-700">
                    Schedule label
                  </span>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="Acme Corp — monthly"
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                    disabled={busy !== undefined}
                  />
                  <span className="text-xs text-slate-500">
                    Used in the email subject. Defaults to the GA4 property name.
                  </span>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-slate-700">GA4 property</span>
                  <select
                    value={form.propertyId}
                    onChange={(e) =>
                      setForm({ ...form, propertyId: e.target.value })
                    }
                    required
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                    disabled={busy !== undefined || !ga4Connection}
                  >
                    <option value="">— select —</option>
                    {ga4Properties.map((p) => (
                      <option key={p.propertyId} value={p.propertyId}>
                        {p.displayName}
                      </option>
                    ))}
                  </select>
                  {!ga4Connection && (
                    <span className="text-xs text-red-700">
                      Connect Google Analytics first.
                    </span>
                  )}
                </label>
                <label className="col-span-full flex flex-col gap-1 text-sm">
                  <span className="font-medium text-slate-700">
                    Recipients (comma or newline separated)
                  </span>
                  <textarea
                    value={form.recipients}
                    onChange={(e) =>
                      setForm({ ...form, recipients: e.target.value })
                    }
                    placeholder="ceo@acmecorp.com, marketing@acmecorp.com"
                    rows={2}
                    required
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
                    disabled={busy !== undefined}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-slate-700">
                    Day of month (1–28)
                  </span>
                  <select
                    value={form.dayOfMonth}
                    onChange={(e) =>
                      setForm({ ...form, dayOfMonth: Number(e.target.value) })
                    }
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                    disabled={busy !== undefined}
                  >
                    {DAYS_OF_MONTH.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-slate-500">
                    First report fires at 02:00 UTC on this day next month.
                  </span>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-slate-700">
                    Subject override (optional)
                  </span>
                  <input
                    type="text"
                    value={form.subjectOverride}
                    onChange={(e) =>
                      setForm({ ...form, subjectOverride: e.target.value })
                    }
                    placeholder="(default: <label> — <date range>)"
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                    disabled={busy !== undefined}
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={busy !== undefined}>
                  Create schedule
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setCreating(false)}
                  disabled={busy !== undefined}
                >
                  Cancel
                </Button>
                {busy && (
                  <span className="self-center text-sm text-slate-500">{busy}</span>
                )}
              </div>
            </form>
          )}

          {list.length === 0 && !creating && (
            <p className="text-sm text-slate-500">
              No schedules yet. Make sure you've configured Settings → Email
              Delivery + connected at least one GA4 property, then click{" "}
              <strong>New schedule</strong>.
            </p>
          )}

          {list.map((s) => (
            <div
              key={s.id}
              className="mb-3 rounded-md border border-slate-200 p-4 text-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium text-slate-900">{s.label}</span>
                  <span
                    className={`ml-3 inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${statusPillClasses(s.status)}`}
                  >
                    {s.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    onClick={() => void toggleRuns(s.id)}
                    disabled={busy !== undefined}
                    className="text-brand-700 underline"
                  >
                    {runsOpen === s.id ? "Hide history" : "Run history"}
                  </button>
                  <button
                    onClick={() => void onDelete(s)}
                    disabled={busy !== undefined}
                    className="text-red-700 underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-1 text-xs md:grid-cols-3">
                <dt className="text-slate-500">Cadence</dt>
                <dd className="md:col-span-2 text-slate-800">
                  {fmtFrequency(s)}
                </dd>
                <dt className="text-slate-500">Recipients</dt>
                <dd className="md:col-span-2 truncate text-slate-800">
                  {s.recipients.join(", ")}
                </dd>
                <dt className="text-slate-500">Next run</dt>
                <dd className="md:col-span-2 text-slate-800">
                  {s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : "—"}
                </dd>
                {s.lastRunAt && (
                  <>
                    <dt className="text-slate-500">Last run</dt>
                    <dd className="md:col-span-2 text-slate-800">
                      {new Date(s.lastRunAt).toLocaleString()}
                    </dd>
                  </>
                )}
              </dl>

              {runsOpen === s.id && (
                <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Run history
                  </h4>
                  {(runs[s.id]?.length ?? 0) === 0 ? (
                    <p className="text-xs text-slate-500">
                      No runs yet. The first run fires at the next scheduled day.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {runs[s.id]!.map((r) => (
                        <li
                          key={r.id}
                          className="rounded border border-slate-200 bg-white p-2 text-xs"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">
                              {new Date(r.startedAt).toLocaleString()}
                            </span>
                            <span
                              className={`rounded-full px-2 py-0.5 ${runStatusClasses(r.status)}`}
                            >
                              {r.status}
                            </span>
                          </div>
                          <p className="mt-1 text-slate-700">{r.message}</p>
                          {r.pdfBytes && (
                            <p className="mt-1 text-slate-500">
                              PDF size: {(r.pdfBytes / 1024).toFixed(1)} KB
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </section>
  );
}
